'use client';

import { Fragment, useMemo } from 'react';

import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { getQuoteLineMtsLocalMap } from '../../../../utils/lead-time';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { EstimatedDelivery } from '../../../shared/quote-pricing/estimated-delivery';
import { QuoteProductPriceItem } from '../models/quote-product-price-item.model';

interface Props {
   products: QuoteProductPriceItem[];
   productDetailBaseRoute: string;
   documentsAnchor: string;
   leadTimeEnabled?: boolean;
   deliveryAddressId?: number;
   onViewPrices: (productId: string) => void;
}

const QuoteProductMobileListItem = ({
   product,
   productDetailBaseRoute,
   documentsAnchor,
   hasMtsLocal,
   onViewPrices
}: {
   product: QuoteProductPriceItem;
   productDetailBaseRoute: string;
   documentsAnchor: string;
   hasMtsLocal: boolean;
   onViewPrices: (productId: string) => void;
}) => {
   const translate = useTranslations();
   const locale = usePathLocale();

   const showLink = !!product.slug && !!productDetailBaseRoute;
   const productDetailRoute = { url: productDetailBaseRoute, noFollow: true };

   const slug = useMemo(() => {
      if (!product.slug) return undefined;

      const searchParams = new URLSearchParams();
      searchParams.set('from', 'quote');
      if (product.colorId != null) searchParams.set('colorId', String(product.colorId));
      if (product.producerColorCode) searchParams.set('colorCode', product.producerColorCode);
      return `${product.slug}?${searchParams.toString()}`;
   }, [product]);

   return (
      <Box direction="column" gap="sm">
         <Box gap="sm" items="start">
            <BoxItem>
               <Avatar shape="square" src={product.logoUrl} title={product.brandName} />
            </BoxItem>
            <BoxItem grow={1} basis={0}>
               <Box direction="column" gap="3xs" items="start">
                  <Typography variant="body-highlight">{product.description || '-'}</Typography>
                  <Typography variant="small" color="subtle">
                     {translate('product.card.product-ref')}: {product.customerReferences.length ? product.customerReferences.join(' • ') : '-'}
                  </Typography>
                  {showLink && slug ? (
                     <DynamicPageLink
                        id={`${product.id}-documents`}
                        baseRoute={productDetailRoute}
                        locale={locale}
                        slug={slug}
                        anchor={documentsAnchor}
                     >
                        <Box gap="2xs" items="center">
                           <Icon icon="file-alt" color="inherit" size="small" />
                           <Typography variant="small" color="primary" underline>
                              {translate('prices.related-documents')}
                           </Typography>
                        </Box>
                     </DynamicPageLink>
                  ) : null}
               </Box>
            </BoxItem>
         </Box>
         {hasMtsLocal && <EstimatedDelivery hasMtsLocal justify="start" />}
         <Button variant="secondary" fullWidth onClick={() => onViewPrices(product.id)}>
            {translate('prices.view-quote-prices')}
         </Button>
      </Box>
   );
};

export const QuoteProductMobileList = ({
   products,
   productDetailBaseRoute,
   documentsAnchor,
   leadTimeEnabled = false,
   deliveryAddressId,
   onViewPrices
}: Props) => {
   const translate = useTranslations();
   const productMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return Object.fromEntries(
         products.map((product) => {
            const lineMtsLocalMap = getQuoteLineMtsLocalMap(product.leadTimeIndicators, product.quotationLines, {
               colorId: product.colorId,
               producerColorCode: product.producerColorCode,
               addressId: deliveryAddressId
            });

            return [product.id, Object.values(lineMtsLocalMap).some(Boolean)];
         })
      );
   }, [products, leadTimeEnabled, deliveryAddressId]);

   return (
      <Paper>
         <Box direction="column" gap="md">
            <Box justify="between" items="center" gap="sm">
               <Typography variant="body-highlight" color="subtle">
                  {translate('prices.your-quoted-products')}
               </Typography>
               <Typography variant="small" color="subtle">
                  {translate('prices.products-available', { total: products.length })}
               </Typography>
            </Box>

            <Box direction="column" gap="md">
               {products.map((product, index) => (
                  <Fragment key={product.id}>
                     {index > 0 && <Divider />}
                     <QuoteProductMobileListItem
                        product={product}
                        productDetailBaseRoute={productDetailBaseRoute}
                        documentsAnchor={documentsAnchor}
                        hasMtsLocal={productMtsLocalMap[product.id] === true}
                        onViewPrices={onViewPrices}
                     />
                  </Fragment>
               ))}
            </Box>
         </Box>
      </Paper>
   );
};
